import React from "react";
import { alpha, Theme } from "@mui/material/styles";
import { ArcStack, ArcText } from "@src/core/ui";
import { arcDateWithTime } from "@src/core/extensions";
import { OrderStatusColors, OrderStatusEnum, OrderStatusNames } from "@src/features/order/entities";
import { ArcOrderCardDetailsProps } from "./types";

const STATUS_LABEL_BG: Partial<Record<OrderStatusEnum, (theme: Theme) => string>> = {
  [OrderStatusEnum.COMPLETED]: (theme) => theme.palette.background.chip ?? "#35364A",
  [OrderStatusEnum.CANCELLED]: (theme) => alpha(theme.palette.divider, 0.24),
  [OrderStatusEnum.ERROR]: (theme) => alpha(theme.palette.error.main, 0.24),
};

const STATUS_LABEL_COLOR: Partial<Record<OrderStatusEnum, string>> = {
  [OrderStatusEnum.CANCELLED]: "text.secondary",
};

const ArcOrderCardDetails: React.FC<ArcOrderCardDetailsProps> = ({
  order,
  fullWidth = true,
  isInFlight = false,
  status,
}) => {
  const getOrderData = () => {
    const orderData = order as any;
    // If it has orders array (Orders tab structure), get the first order
    if (orderData.orders && orderData.orders.length > 0) {
      return {
        id: orderData.orders[0].id,
        missionId: orderData.missionId,
        createDate: orderData.orders[0].createDate,
        deliveryLocation: orderData.orders[0].deliveryLocation,
        clientName: orderData.orders[0].clientName,
        status: orderData.orders[0].status,
      };
    }
    // Otherwise, use direct order data (History tab structure)
    return {
      id: orderData.id,
      missionId: orderData.missionId,
      createDate: orderData.createDate,
      deliveryLocation: orderData.deliveryLocation,
      clientName: orderData.clientName,
      status: orderData.status,
    };
  };

  const orderData = getOrderData();

  if (fullWidth) {
    // Full width layout (History tab) - original layout
    return (
      <ArcStack direction="column" spacing={1}>
        <ArcStack direction="row" justifyContent="space-between">
          <ArcText.h6 color="white" fontSize={14}>
            #{orderData.id || orderData.missionId?.slice(0, 8) + "..." || "N/A"}
          </ArcText.h6>
          <ArcText color="text.secondary" fontSize={12}>
            {arcDateWithTime(orderData.createDate)}
          </ArcText>
        </ArcStack>
        <ArcStack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <ArcText
            color="text.secondary"
            fontSize={12}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {orderData.deliveryLocation?.address || "Unknown destination"}
          </ArcText>
          {status ? (
            <ArcText
              fontSize={10}
              fontWeight={400}
              color={STATUS_LABEL_COLOR[status] ?? OrderStatusColors[status]}
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                whiteSpace: "nowrap",
                bgcolor: STATUS_LABEL_BG[status],
              }}
            >
              {OrderStatusNames[status]}
            </ArcText>
          ) : null}
        </ArcStack>
      </ArcStack>
    );
  } else {
    // Column layout (Orders tab) - date, space, order ID, address
    return (
      <ArcStack direction="column">
        <ArcText color="text.secondary" fontSize={12}>
          {arcDateWithTime(orderData.createDate)}
        </ArcText>
        <ArcStack>
          <ArcText.h6 color="white" fontSize={12} fontWeight={600} mt={'8px'}>
            #{orderData.id || orderData.missionId?.slice(0, 8) + "..." || "N/A"}
          </ArcText.h6>
        </ArcStack>
        <ArcText
          color="text.secondary"
          fontSize={12}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            marginTop: "4px",
          }}
        >
          {orderData.deliveryLocation?.address || "Unknown destination"}
        </ArcText>
        {isInFlight && (
          <ArcText color="text.secondary" fontSize={12} marginTop={'8px'}>
            {order?.drone?.name}
          </ArcText>
        )}
      </ArcStack>
    );
  }
};

export default ArcOrderCardDetails;
